import React from 'react'

type Props = {
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
}

/**
 * This hook manages delayed opening and closing of a component.
 * It can be used controlled or uncontrolled.
 * If used uncontrolled, the isOpen prop should be passed to the hook.
 * Otherwise the passed isOpen will always just be returned.
 * onOpenChange will be called when the isOpen state changes or should change.
 */
const useOpenWithDelays = ({isOpen, onOpenChange}: Props = {}) => {
  const openTimeout = React.useRef<NodeJS.Timeout>(null)
  const closeTimeout = React.useRef<NodeJS.Timeout>(null)
  const openTimeRef = React.useRef<number>(0)

  const [innerIsOpen, setInnerIsOpen] = React.useState<boolean>(false)

  const clearTimeouts = () => {
    if (openTimeout.current) {
      clearTimeout(openTimeout.current)
    }
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
    }
  }

  const openImmediately = (autoCloseAfter?: number) => {
    if (!innerIsOpen) {
      openTimeRef.current = Date.now()
      setInnerIsOpen(true)
      onOpenChange?.(true)
    }

    if (autoCloseAfter !== undefined) {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current)
      }

      closeTimeout.current = setTimeout(() => {
        setInnerIsOpen(false)
        onOpenChange?.(false)
      }, autoCloseAfter)
    }
  }

  const closeImmediately = () => {
    setInnerIsOpen(false)
    onOpenChange?.(false)
    openTimeRef.current = 0
  }

  const open = ({
    delay = 0, // Open with a delay
    autoCloseAfter, // Auto close after a delay
  }: {delay?: number; autoCloseAfter?: number} = {}) => {
    clearTimeouts()

    if (delay !== undefined) {
      openTimeout.current = setTimeout(() => {
        openImmediately(autoCloseAfter)
      }, delay)
    } else {
      openImmediately(autoCloseAfter)
    }
  }

  const close = ({
    delay = 0, // Close with a delay
    keepOpenDelay = 0, // Even if close is called, keep open for a delay
  }: {delay?: number; keepOpenDelay?: number} = {}) => {
    clearTimeouts()

    const now = Date.now()
    const timeout = Math.max(now + delay, openTimeRef.current + keepOpenDelay) - now

    if (timeout > 0) {
      closeTimeout.current = setTimeout(() => {
        closeImmediately()
      }, timeout)
    } else {
      closeImmediately()
    }
  }

  React.useEffect(() => clearTimeouts, [])

  const actualIsOpen = isOpen !== undefined ? isOpen : innerIsOpen

  return {
    isOpen: actualIsOpen,
    open,
    close,
  }
}

export default useOpenWithDelays
